(() => {
  const SELECTOR = '[data-contract-revocation-modal]';
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'textarea:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  const initializeModal = (modal) => {
    if (!(modal instanceof HTMLElement) || modal.dataset.contractRevocationInitialized === 'true') return;

    const dialog = modal.querySelector('[data-contract-revocation-dialog]');
    const form = modal.closest('form');
    const formView = modal.querySelector('[data-contract-revocation-view="form"]');
    const successView = modal.querySelector('[data-contract-revocation-view="success"]');
    const formHeading = modal.querySelector('[data-contract-revocation-heading]');
    const successHeading = modal.querySelector('[data-contract-revocation-success-heading]');
    const formDescription = modal.querySelector('.contract-revocation__intro');
    const successDescription = modal.querySelector('[data-contract-revocation-success-description]');
    const serverError = modal.querySelector('[data-contract-revocation-server-error]');
    const clientError = modal.querySelector('[data-contract-revocation-client-error]');
    const submitButton = modal.querySelector('[data-contract-revocation-submit]');
    const closeButtons = [...modal.querySelectorAll('[data-contract-revocation-close]')];
    const formId = modal.dataset.contractRevocationFormId || form?.id || '';
    const storageKey = `oe-contract-revocation-pending:${formId}`;
    let previouslyFocused = null;
    let currentView = 'form';
    let isSubmitting = false;

    if (!dialog || !form || !formView || !successView || !submitButton || !formId) return;

    modal.dataset.contractRevocationInitialized = 'true';
    form.noValidate = true;

    const triggers = [...document.querySelectorAll('[data-contract-revocation-trigger]')].filter(
      (trigger) => trigger.getAttribute('aria-controls') === modal.id,
    );

    const getStoredSubmission = () => {
      try {
        const timestamp = Number(sessionStorage.getItem(storageKey));
        sessionStorage.removeItem(storageKey);
        return Number.isFinite(timestamp) && Date.now() - timestamp < 5 * 60 * 1000;
      } catch (_error) {
        return false;
      }
    };

    const hasMatchingHash = () => {
      try {
        return decodeURIComponent(window.location.hash.slice(1)) === formId;
      } catch (_error) {
        return window.location.hash.slice(1) === formId;
      }
    };

    const setView = (view) => {
      currentView = view === 'success' ? 'success' : 'form';
      formView.hidden = currentView !== 'form';
      successView.hidden = currentView !== 'success';
      if (serverError) serverError.hidden = view !== 'error';
      if (view !== 'error' && clientError) clientError.hidden = true;
      const activeHeading = currentView === 'success' ? successHeading : formHeading;
      const activeDescription = currentView === 'success' ? successDescription : formDescription;
      if (activeHeading?.id) dialog.setAttribute('aria-labelledby', activeHeading.id);
      if (activeDescription?.id) dialog.setAttribute('aria-describedby', activeDescription.id);
    };

    const getFocusableElements = () =>
      [...dialog.querySelectorAll(FOCUSABLE_SELECTOR)].filter(
        (element) => element instanceof HTMLElement && !element.hidden && element.offsetParent !== null,
      );

    const handleKeydown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (!dialog.contains(document.activeElement)) {
        event.preventDefault();
        firstElement.focus();
      } else if (event.shiftKey && (document.activeElement === firstElement || document.activeElement === dialog)) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    const openModal = (trigger, view = 'form') => {
      previouslyFocused =
        trigger instanceof HTMLElement
          ? trigger
          : triggers.find((item) => item.offsetParent !== null) || triggers[0] || document.activeElement;
      setView(view);
      modal.hidden = false;
      document.body.classList.add('contract-revocation-open');
      triggers.forEach((item) => item.setAttribute('aria-expanded', 'true'));
      document.addEventListener('keydown', handleKeydown);

      requestAnimationFrame(() => {
        dialog.focus({ preventScroll: true });
      });
    };

    const consumeSuccessUrl = () => {
      try {
        const url = new URL(window.location.href);
        if (url.searchParams.get('contact_posted') === 'true') url.searchParams.delete('contact_posted');
        if (hasMatchingHash()) url.hash = '';
        window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
      } catch (_error) {
        // The success modal still works if the URL cannot be cleaned up.
      }
    };

    const setSubmitting = (submitting) => {
      isSubmitting = submitting;
      submitButton.disabled = submitting;
      if (submitting) submitButton.setAttribute('aria-busy', 'true');
      else submitButton.removeAttribute('aria-busy');
    };

    function closeModal() {
      if (modal.hidden) return;

      modal.hidden = true;
      document.body.classList.remove('contract-revocation-open');
      triggers.forEach((item) => item.setAttribute('aria-expanded', 'false'));
      document.removeEventListener('keydown', handleKeydown);

      if (currentView === 'success') {
        form.reset();
        setSubmitting(false);
        setView('form');
      }

      if (previouslyFocused instanceof HTMLElement && previouslyFocused.isConnected) {
        previouslyFocused.focus({ preventScroll: true });
      }
    }

    const fieldMessage = (input) => {
      if (input.validity.valueMissing) return input.dataset.requiredMessage || 'Bitte füllen Sie dieses Pflichtfeld aus.';
      if (input.validity.typeMismatch) return input.dataset.invalidMessage || 'Bitte überprüfen Sie Ihre Eingabe.';
      return input.validationMessage || 'Bitte überprüfen Sie Ihre Eingabe.';
    };

    const validateField = (input) => {
      const field = input.closest('[data-contract-revocation-field]');
      const error = field?.querySelector('[data-contract-revocation-field-error]');
      const isValid = input.validity.valid;

      field?.classList.toggle('is-invalid', !isValid);
      input.setAttribute('aria-invalid', String(!isValid));
      if (error) {
        error.textContent = isValid ? '' : fieldMessage(input);
        error.hidden = isValid;
      }
      return isValid;
    };

    triggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        openModal(trigger, 'form');
      });
    });

    closeButtons.forEach((button) => button.addEventListener('click', closeModal));
    modal.addEventListener('click', (event) => {
      if (event.target === modal) closeModal();
    });

    [...form.elements].forEach((input) => {
      if (!(input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement || input instanceof HTMLSelectElement)) return;
      if (input.type === 'hidden' || input.type === 'submit') return;
      input.addEventListener('blur', () => validateField(input));
      input.addEventListener('input', () => {
        if (input.getAttribute('aria-invalid') === 'true') validateField(input);
      });
      input.addEventListener('change', () => {
        if (input.getAttribute('aria-invalid') === 'true') validateField(input);
      });
    });

    const validateForm = () => {
      const fields = [...form.elements].filter((input) => {
        const isFormField =
          input instanceof HTMLInputElement ||
          input instanceof HTMLTextAreaElement ||
          input instanceof HTMLSelectElement;
        return isFormField && input.type !== 'hidden' && input.type !== 'submit';
      });
      let firstInvalidField = null;
      fields.forEach((input) => {
        if (!validateField(input) && !firstInvalidField) firstInvalidField = input;
      });

      if (firstInvalidField) {
        if (clientError) clientError.hidden = false;
        firstInvalidField.focus();
        return false;
      }

      if (clientError) clientError.hidden = true;
      return true;
    };

    const rememberSubmission = () => {
      try {
        sessionStorage.setItem(storageKey, String(Date.now()));
      } catch (_error) {
        // The form remains fully functional when session storage is unavailable.
      }
    };

    submitButton.addEventListener('click', () => {
      if (isSubmitting) return;
      if (!validateForm()) return;
      setSubmitting(true);
      rememberSubmission();
      form.requestSubmit();
    });

    form.addEventListener('submit', (event) => {
      if (!validateForm()) {
        event.preventDefault();
        setSubmitting(false);
        return;
      }
      if (!isSubmitting) {
        setSubmitting(true);
        rememberSubmission();
      }
    });

    form.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' || event.target instanceof HTMLTextAreaElement) return;
      event.preventDefault();
      submitButton.click();
    });

    const serverState = modal.dataset.contractRevocationServerState;
    const hasStoredSubmission = getStoredSubmission();
    const hasResponseHash = window.location.hash.length > 1;
    const ownsServerResponse = hasMatchingHash() || (!hasResponseHash && hasStoredSubmission);
    if (ownsServerResponse && (serverState === 'success' || serverState === 'error')) {
      openModal(null, serverState);
      if (serverState === 'success') requestAnimationFrame(consumeSuccessUrl);
    }
  };

  const initializeAll = (root = document) => {
    if (root instanceof HTMLElement && root.matches(SELECTOR)) initializeModal(root);
    root.querySelectorAll?.(SELECTOR).forEach(initializeModal);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', () => {
    document.body.classList.remove('contract-revocation-open');
  });
})();
